import {
    ReleaseNotesHeader,
    TableCellContent,
    ComponentLinks,
    NoteCell,
    CodeList,
    LinkRow
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    Paragraph,
    DocsTable,
    SubTitle,
    Article,
    Callout,
    Section
} from "../../components/content";

type ReplacedRow = {
    old: Array<string>;
    noteKey: string;
    next: string;
};

type RenamedRow = {
    components: Array<string>;
    next: Array<string>;
    old: Array<string>;
};

type NoEffectRow = {
    components: Array<string>;
    props: Array<string>;
    nowKey: string;
};

type KeyRow = {
    next: Array<string>;
    old: Array<string>;
    noteKey?: string;
};

const FIELDS = [
    "TextInput",
    "TextAreaInput",
    "NumericInput",
    "SelectBox",
    "DateTimePicker"
];

const TEXT_FIELDS = [
    "TextInput",
    "TextAreaInput",
    "NumericInput"
];

const SWITCHERS = [
    "ThemeSwitcher",
    "PaletteSwitcher",
    "LocaleSwitcher"
];

const REPLACED_ROWS: Array<ReplacedRow> = [
    {
        noteKey: "deprecatedPage.noteStateCard",
        old: [
            "StateCard"
        ],
        next: "Empty"
    },
    {
        noteKey: "deprecatedPage.noteRowCard",
        old: [
            "RowCard"
        ],
        next: "Item"
    },
    {
        noteKey: "deprecatedPage.noteSticker",
        next: "Badge",
        old: [
            "Sticker"
        ]
    },
    {
        noteKey: "deprecatedPage.noteNotificationIndicator",
        old: [
            "NotificationIndicator"
        ],
        next: "Badge.Indicator"
    },
    {
        noteKey: "deprecatedPage.noteSnackBar",
        old: [
            "SnackBar",
            "NCoreUIKitSnackBar"
        ],
        next: "Toast"
    },
    {
        noteKey: "deprecatedPage.noteSnackBarLayer",
        old: [
            "name=\"snack-bar-system\""
        ],
        next: "Toast"
    },
    {
        noteKey: "deprecatedPage.noteLoadingIcon",
        old: [
            "LoadingIcon"
        ],
        next: "Loading"
    },
    {
        noteKey: "deprecatedPage.noteSeperator",
        next: "Separator",
        old: [
            "Seperator"
        ]
    }
];

const RENAMED_ROWS: Array<RenamedRow> = [
    {
        old: [
            "initialSelectedItems"
        ],
        next: [
            "defaultValue",
            "value",
            "onValueChange"
        ],
        components: [
            "SelectBox"
        ]
    },
    {
        components: [
            "Popover",
            "HoverCard",
            "Tooltip",
            "Dialog",
            "Sheet"
        ],
        old: [
            "isVisible"
        ],
        next: [
            "isOpen"
        ]
    },
    {
        next: [
            "onOpenChange"
        ],
        components: [
            "Popover",
            "HoverCard",
            "Tooltip",
            "Sheet"
        ],
        old: [
            "onOpen",
            "onClose"
        ]
    },
    {
        components: [
            "Command.Dialog"
        ],
        old: [
            "isVisible"
        ],
        next: [
            "isOpen"
        ]
    },
    {
        components: [
            "Command.Dialog"
        ],
        old: [
            "onOpenRequest"
        ],
        next: [
            "onOpenChange"
        ]
    },
    {
        components: [
            "Modal",
            "BottomSheet",
            "SelectSheet",
            "DateTimeSheet"
        ],
        old: [
            "isActive"
        ],
        next: [
            "isOpen"
        ]
    },
    {
        next: [
            "onOpenChange"
        ],
        components: [
            "BottomSheet"
        ],
        old: [
            "onClose"
        ]
    },
    {
        components: [
            "BottomSheet"
        ],
        next: [
            "onOpened"
        ],
        old: [
            "onOpen"
        ]
    },
    {
        components: [
            "Tabs",
            "Accordion",
            "ToggleGroup",
            "Slider"
        ],
        next: [
            "defaultValue"
        ],
        old: [
            "initialValue"
        ]
    },
    {
        next: [
            "onValueChange"
        ],
        components: [
            "Tabs",
            "Accordion",
            "ToggleGroup",
            "Slider"
        ],
        old: [
            "onChange"
        ]
    },
    {
        old: [
            "iconDirection=\"left\" | \"right\""
        ],
        next: [
            "iconDirection=\"start\" | \"end\""
        ],
        components: [
            "Button"
        ]
    },
    {
        next: [
            "android_disableSound"
        ],
        old: [
            "touchSoundDisabled"
        ],
        components: [
            "Button"
        ]
    },
    {
        old: [
            "iconPosition=\"left\" | \"right\""
        ],
        next: [
            "iconPosition=\"start\" | \"end\""
        ],
        components: [
            "Sticker"
        ]
    },
    {
        components: [
            "NotificationIndicator"
        ],
        next: [
            "location.start",
            "location.end"
        ],
        old: [
            "location.right",
            "location.left"
        ]
    },
    {
        old: [
            "location"
        ],
        components: [
            "Tooltip"
        ],
        next: [
            "align",
            "side"
        ]
    },
    {
        next: [
            "orientation"
        ],
        components: [
            "Separator"
        ],
        old: [
            "direction"
        ]
    },
    {
        next: [
            "isChecked"
        ],
        old: [
            "isActive"
        ],
        components: [
            "Switch"
        ]
    },
    {
        old: [
            "isChecked=\"checked\" | \"partially\""
        ],
        next: [
            "isChecked={true}",
            "isIndeterminate"
        ],
        components: [
            "CheckBox"
        ]
    },
    {
        next: [
            "type=\"neutral\""
        ],
        old: [
            "type=\"default\""
        ],
        components: FIELDS
    },
    {
        next: [
            "type=\"neutral\"",
            "isShowHintTextIcon"
        ],
        old: [
            "type=\"question\""
        ],
        components: FIELDS
    },
    {
        next: [
            "isClearEnabled"
        ],
        old: [
            "isCleanEnabled"
        ],
        components: FIELDS
    },
    {
        next: [
            "clearIconStyle"
        ],
        old: [
            "cleanIconStyle"
        ],
        components: FIELDS
    },
    {
        components: TEXT_FIELDS,
        next: [
            "ref.clearText()"
        ],
        old: [
            "ref.cleanText()"
        ]
    },
    {
        next: [
            "ref.clearSelections()",
            "ref.clearAll()"
        ],
        old: [
            "ref.cleanSelections()",
            "ref.cleanAll()"
        ],
        components: [
            "SelectBox"
        ]
    },
    {
        components: [
            "DateTimePicker"
        ],
        next: [
            "ref.clear()"
        ],
        old: [
            "ref.clean()"
        ]
    },
    {
        components: [
            "SelectSheet"
        ],
        next: [
            "clearAll"
        ],
        old: [
            "cleanAll"
        ]
    },
    {
        components: [
            "DateTimeSheet"
        ],
        next: [
            "clear"
        ],
        old: [
            "clean"
        ]
    },
    {
        next: [
            "isShowCalendarChangerTool"
        ],
        old: [
            "isShowCalenderChangerTool"
        ],
        components: [
            "DateTimePicker",
            "DateTimeSheet"
        ]
    },
    {
        old: [
            "isShowStatusIndicatorSplicer"
        ],
        next: [
            "isShowStatusIndicatorBorder"
        ],
        components: [
            "Avatar",
            "AvatarGroup"
        ]
    },
    {
        old: [
            "size=\"xxLarge\" | \"xxxLarge\""
        ],
        next: [
            "size=\"xLarge\""
        ],
        components: [
            "Avatar",
            "AvatarGroup"
        ]
    },
    {
        old: [
            "size=\"xxSmall\""
        ],
        next: [
            "size=\"small\""
        ],
        components: [
            "SiteLogo"
        ]
    },
    {
        next: [
            "size=\"medium\""
        ],
        old: [
            "size=\"xSmall\""
        ],
        components: [
            "SiteLogo"
        ]
    },
    {
        old: [
            "size=\"xLarge\" | \"xxLarge\" | \"xxxLarge\""
        ],
        next: [
            "size=\"large\""
        ],
        components: [
            "SiteLogo"
        ]
    },
    {
        old: [
            "isWorkWithNextShowSystem"
        ],
        next: [
            "isShowNextVariant"
        ],
        components: SWITCHERS
    },
    {
        next: [
            "isWrapSafeAreaContext"
        ],
        old: [
            "isWrapSafeareaContext"
        ],
        components: [
            "Header",
            "PageContainer"
        ]
    },
    {
        next: [
            "isGoBackEnabled"
        ],
        old: [
            "isGoBackEnable"
        ],
        components: [
            "Header"
        ]
    },
    {
        next: [
            "isWorkWithSeparator"
        ],
        old: [
            "isWorkWithSeperator"
        ],
        components: [
            "MainHeader"
        ]
    },
    {
        next: [
            "isShowCollapseButton"
        ],
        old: [
            "isShowCollabsButton"
        ],
        components: [
            "Menu",
            "EmbeddedMenu"
        ]
    },
    {
        next: [
            "renderHeaderSeparator"
        ],
        old: [
            "renderHeaderSeperator"
        ],
        components: [
            "Menu",
            "EmbeddedMenu"
        ]
    },
    {
        next: [
            "separatorProps"
        ],
        old: [
            "seperatorProps"
        ],
        components: [
            "Menu"
        ]
    },
    {
        next: [
            "updateMenuButtonCollapse"
        ],
        old: [
            "updateMenuButtonCollabs"
        ],
        components: [
            "MenuButton"
        ]
    }
];

const NO_EFFECT_ROWS: Array<NoEffectRow> = [
    {
        nowKey: "deprecatedPage.nowLoading",
        props: [
            "displayBehaviourWhileLoading"
        ],
        components: [
            "Button",
            "CheckBox",
            "RadioButton",
            "Switch",
            "Chip"
        ]
    },
    {
        nowKey: "deprecatedPage.nowKeyboard",
        props: [
            "isAutoKeyboardDismissOnBlur"
        ],
        components: TEXT_FIELDS
    },
    {
        nowKey: "deprecatedPage.nowHintIcon",
        props: [
            "hintTextIconStyle"
        ],
        components: [
            "SelectBox",
            "DateTimePicker"
        ]
    },
    {
        nowKey: "deprecatedPage.nowNothing",
        components: [
            "DateTimePicker"
        ],
        props: [
            "isSearchable"
        ]
    },
    {
        nowKey: "deprecatedPage.nowSafeArea",
        props: [
            "isInlineSafeArea"
        ],
        components: [
            "SnackBar"
        ]
    },
    {
        nowKey: "deprecatedPage.nowOpaque",
        props: [
            "glassEffect"
        ],
        components: [
            "MainHeader"
        ]
    },
    {
        nowKey: "deprecatedPage.nowSwitcher",
        props: [
            "isWorkWithHighlightButton"
        ],
        components: SWITCHERS
    },
    {
        nowKey: "deprecatedPage.nowSingleValue",
        components: [
            "DateTimePicker",
            "DateTimeSheet"
        ],
        props: [
            "maxChoice"
        ]
    },
    {
        nowKey: "deprecatedPage.nowNoRecurrence",
        props: [
            "variant=\"rrule\""
        ],
        components: [
            "DateTimeSheet"
        ]
    },
    {
        nowKey: "deprecatedPage.nowSiteLogo",
        components: [
            "Menu",
            "EmbeddedMenu"
        ],
        props: [
            "logo"
        ]
    },
    {
        nowKey: "deprecatedPage.nowEntryCollapsible",
        props: [
            "isCollapsible"
        ],
        components: [
            "Menu",
            "EmbeddedMenu"
        ]
    },
    {
        nowKey: "deprecatedPage.nowAlwaysRendered",
        props: [
            "isContentRequired"
        ],
        components: [
            "Modal"
        ]
    },
    {
        nowKey: "deprecatedPage.nowNothing",
        props: [
            "actions[].titleProps"
        ],
        components: [
            "SystemToolbar"
        ]
    },
    {
        nowKey: "deprecatedPage.nowLegacy",
        props: [
            "leftContainerProps",
            "centerContainerProps",
            "rightContainerProps",
            "linkContainerProps",
            "leftMarkdownProps",
            "centerMarkdownProps",
            "rightMarkdownProps",
            "linkMarkdownProps"
        ],
        components: [
            "MarkdownViewer"
        ]
    }
];

const TYPO_ROWS: Array<KeyRow> = [
    {
        old: [
            "content.text.constrastHigh",
            "constrastMid",
            "constrastLow"
        ],
        next: [
            "content.text.contrastHigh",
            "contrastMid",
            "contrastLow"
        ]
    },
    {
        old: [
            "content.icon.constrastHigh",
            "constrastMid",
            "constrastLow"
        ],
        next: [
            "content.icon.contrastHigh",
            "contrastMid",
            "contrastLow"
        ]
    },
    {
        next: [
            "borders.separator"
        ],
        old: [
            "borders.seperator"
        ]
    }
];

const ALIAS_ROWS: Array<KeyRow> = [
    {
        old: [
            "system.onMid.surface"
        ],
        next: [
            "content.text.mid"
        ]
    },
    {
        old: [
            "system.onLow.surface"
        ],
        next: [
            "content.text.low"
        ]
    },
    {
        next: [
            "content.container.subtle"
        ],
        old: [
            "system.variant.surface"
        ]
    },
    {
        old: [
            "system.onVariant.surface"
        ],
        next: [
            "content.text.mid"
        ]
    },
    {
        next: [
            "content.text.high"
        ],
        old: [
            "system.on.surface"
        ]
    },
    {
        old: [
            "system.disabled.content"
        ],
        next: [
            "content.text.disabled"
        ]
    },
    {
        old: [
            "system.state.content.mid.error"
        ],
        next: [
            "content.text.danger"
        ]
    }
];

const OTHER_ROWS: Array<KeyRow> = [
    {
        noteKey: "deprecatedPage.noteType",
        next: [
            "ISeparatorProps"
        ],
        old: [
            "ISeperatorProps"
        ]
    },
    {
        noteKey: "deprecatedPage.noteOk",
        next: [
            "okay"
        ],
        old: [
            "ok"
        ]
    }
];

export const getDeprecatedToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("deprecatedPage.componentsTitle"),
        id: "components"
    },
    {
        title: t("deprecatedPage.propsTitle"),
        id: "props"
    },
    {
        title: t("deprecatedPage.noEffectTitle"),
        id: "no-effect"
    },
    {
        title: t("deprecatedPage.themeTitle"),
        id: "theme"
    },
    {
        title: t("deprecatedPage.otherTitle"),
        id: "other"
    }
];

const DeprecatedPage = () => {
    const {
        t
    } = useDocsI18n();

    const renderKeyTable = (rows: Array<KeyRow>) => <DocsTable
        headers={[
            t("deprecatedPage.headOld"),
            t("deprecatedPage.headNew")
        ]}
        rows={rows.map((row) => [
            <TableCellContent>
                <CodeList
                    values={row.old}
                />
            </TableCellContent>,
            <TableCellContent>
                <CodeList
                    values={row.next}
                />
            </TableCellContent>
        ])}
    />;

    return <Article>
        <ReleaseNotesHeader
            lead={t("releaseNotes.deprecatedLead")}
            title={t("page.deprecated")}
        />
        <Callout
            title={t("deprecatedPage.calloutTitle")}
        >
            {t("deprecatedPage.calloutText")}
        </Callout>
        <Section
            title={t("deprecatedPage.componentsTitle")}
            id="components"
        >
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.componentsText")}
            </Paragraph>
            <DocsTable
                headers={[
                    t("deprecatedPage.headOld"),
                    t("deprecatedPage.headInstead"),
                    t("deprecatedPage.headNotes")
                ]}
                rows={REPLACED_ROWS.map((row) => [
                    <TableCellContent>
                        <CodeList
                            values={row.old}
                        />
                    </TableCellContent>,
                    <TableCellContent>
                        <ComponentLinks
                            names={[
                                row.next
                            ]}
                            size="small"
                        />
                    </TableCellContent>,
                    <NoteCell>
                        {t(row.noteKey)}
                    </NoteCell>
                ])}
            />
        </Section>
        <Section
            title={t("deprecatedPage.propsTitle")}
            id="props"
        >
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.propsText")}
            </Paragraph>
            <DocsTable
                headers={[
                    t("deprecatedPage.headComponent"),
                    t("deprecatedPage.headOld"),
                    t("deprecatedPage.headInstead")
                ]}
                rows={RENAMED_ROWS.map((row) => [
                    <TableCellContent>
                        <ComponentLinks
                            names={row.components}
                            size="small"
                        />
                    </TableCellContent>,
                    <TableCellContent>
                        <CodeList
                            values={row.old}
                        />
                    </TableCellContent>,
                    <TableCellContent>
                        <CodeList
                            values={row.next}
                        />
                    </TableCellContent>
                ])}
            />
        </Section>
        <Section
            title={t("deprecatedPage.noEffectTitle")}
            id="no-effect"
        >
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.noEffectText")}
            </Paragraph>
            <DocsTable
                headers={[
                    t("deprecatedPage.headComponent"),
                    t("deprecatedPage.headProp"),
                    t("deprecatedPage.headNow")
                ]}
                rows={NO_EFFECT_ROWS.map((row) => [
                    <TableCellContent>
                        <ComponentLinks
                            names={row.components}
                            size="small"
                        />
                    </TableCellContent>,
                    <TableCellContent>
                        <CodeList
                            values={row.props}
                        />
                    </TableCellContent>,
                    <NoteCell>
                        {t(row.nowKey)}
                    </NoteCell>
                ])}
            />
        </Section>
        <Section
            title={t("deprecatedPage.themeTitle")}
            id="theme"
        >
            <SubTitle>
                {t("deprecatedPage.typoTitle")}
            </SubTitle>
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.typoText")}
            </Paragraph>
            {renderKeyTable(TYPO_ROWS)}
            <SubTitle>
                {t("deprecatedPage.aliasTitle")}
            </SubTitle>
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.aliasText")}
            </Paragraph>
            {renderKeyTable(ALIAS_ROWS)}
            <LinkRow
                items={[
                    {
                        title: t("page.theming"),
                        href: "/theming"
                    },
                    {
                        href: "/foundations/color",
                        title: t("page.color")
                    }
                ]}
                size="small"
            />
        </Section>
        <Section
            title={t("deprecatedPage.otherTitle")}
            id="other"
        >
            <Paragraph
                isMuted={true}
            >
                {t("deprecatedPage.otherText")}
            </Paragraph>
            <DocsTable
                headers={[
                    t("deprecatedPage.headOld"),
                    t("deprecatedPage.headNew"),
                    t("deprecatedPage.headNotes")
                ]}
                rows={OTHER_ROWS.map((row) => [
                    <TableCellContent>
                        <CodeList
                            values={row.old}
                        />
                    </TableCellContent>,
                    <TableCellContent>
                        <CodeList
                            values={row.next}
                        />
                    </TableCellContent>,
                    <NoteCell>
                        {t(row.noteKey ?? "")}
                    </NoteCell>
                ])}
            />
            <Callout
                title={t("deprecatedPage.noAliasTitle")}
            >
                {t("deprecatedPage.noAliasText")}
            </Callout>
        </Section>
    </Article>;
};
export default DeprecatedPage;
